import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Button,
    Box,
    IconButton,
    Typography,
    Grid,
    Alert,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import {
    useCreateUserMutation,
    useEditUserMutation,
    useGetUsersByIdQuery,
    useGetAllPlantsQuery,
    useGetAllRolesQuery,
} from '../../../api/services/userManagementAPI';
import BackdropLoader from '../../../components/common/BackdropLoader';
import Swal from 'sweetalert2';
import { createFormUserSchema, updateFormUserSchema } from './userParser';
import { customZodResolver } from '../../../utils/customZodResolver';

interface UserFormData {
    userName: string;
    email: string;
    mobileno: string;
    employeeId: string;
    password: string;
    phone: string;
    plant: string;
    designation: string;
}

interface UserFormModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    user?: any;
    mode: 'create' | 'edit';
}

interface PlantOption {
    id: number | string;
    label: string;
}

interface RoleOption {
    id: number | string;
    label: string;
}


const UserFormModal: React.FC<UserFormModalProps> = ({
    open,
    onClose,
    onSuccess,
    user,
    mode,
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // API Hooks
    const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
    const [editUser, { isLoading: isEditing }] = useEditUserMutation();

    // Fetch dropdown data
    const { data: plantsData, isLoading: isLoadingPlants } = useGetAllPlantsQuery();
    const { data: rolesData, isLoading: isLoadingRoles } = useGetAllRolesQuery();

    // Fetch user data for edit mode
    const { data: userData, isLoading: isLoadingUser } = useGetUsersByIdQuery(
        user?.id ?? '',
        {
            skip: !user?.id || mode !== 'edit',
        }
    );


    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
        setError,
        clearErrors,
    } = useForm<UserFormData>({
        resolver: customZodResolver(mode === 'edit' ? updateFormUserSchema : createFormUserSchema),
        defaultValues: {
            userName: '',
            email: '',
            password: '',
            plant: '',
            designation: '',
            employeeId: '',
            phone: '',
        },
        mode: 'onChange',
        reValidateMode: 'onChange',
    });
    // console.log(errors, formErrors, '!!errors.userName || !!formErrors.userName');


    // Extract plants and roles from API response
    const plants: PlantOption[] = plantsData?.data || [];
    const designations: RoleOption[] = rolesData?.data || [];

    // Reset form when modal opens or user data changes
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && userData?.user) {
                const formattedUser = userData.user;
                reset({
                    userName: formattedUser.userName || '',
                    email: formattedUser.email || '',
                    password: '',
                    plant: String(formattedUser.plant || ''),
                    designation: String(formattedUser.designation || ''),
                    employeeId: formattedUser.employeeId || '',
                    phone: formattedUser.phone || '',
                });
            } else if (mode === 'create') {
                reset({
                    userName: '',
                    email: '',
                    password: '',
                    plant: '',
                    designation: '',
                    employeeId: '',
                    phone: '',
                });
            }
            // Clear errors when form opens
            setFormErrors({});
            clearErrors();
        }
    }, [open, userData, mode, reset, clearErrors]);

    const onSubmit = async (data: UserFormData) => {
        // Clear previous errors
        setFormErrors({});
        clearErrors();

        // Validate using safeParse
        const validationResult = (mode === 'edit' ? updateFormUserSchema : createFormUserSchema).safeParse(data);

        if (!validationResult.success) {
            // Handle validation errors
            const fieldErrors: Record<string, string> = {};

            validationResult.error.issues.forEach((issue) => {
                const path = issue.path.join('.');
                fieldErrors[path] = issue.message;

                // Set errors for react-hook-form
                if (issue.path.length === 1) {
                    setError(issue.path[0] as keyof UserFormData, {
                        type: 'manual',
                        message: issue.message
                    });
                }
            });

            setFormErrors(fieldErrors);

            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fix the validation errors before submitting.',
                confirmButtonColor: '#003EB0',
                confirmButtonText: 'OK'
            });
            return;
        }

        try {
            const payload: any = {
                userName: data.userName,
                email: data.email,
                plant: Number(data.plant),
                designation: Number(data.designation),
                phone: data.phone,
                employeeId: data.employeeId,
            };

            if (mode === 'create') {
                payload.password = data.password;
                const createRes: any = await createUser(payload).unwrap();
                // console.log(createRes, 'createRes');
                onClose();
                Swal.fire({
                    title: "Success",
                    html: `<p>${createRes?.message}</p>`,
                    icon: 'success',
                });
                // console.log(createRes, 'resresres');

            } else if (mode === 'edit' && user?.id) {
                if (data.password && data?.password.length >= 6) {
                    payload.password = data?.password;
                }
                onClose();
                const updateRes: any = await editUser({ id: user.id, ...payload }).unwrap();

                Swal.fire({
                    title: "Success",
                    html: `<p>${updateRes.message}</p>`,
                    icon: 'success',
                });
            }

            onSuccess?.();
            handleClose();
        } catch (error: any) {
            console.error('Error saving user:', error);
            // onClose();
            Swal.fire({
                text:
                    error?.message || error?.data?.msg ||
                    error?.data?.message ||
                    'Error While Process User Details. Please try again.',
                icon: 'error',
                didOpen: () => {
                    const swalContainer = document.querySelector('.swal2-container');
                    if (swalContainer) swalContainer.setAttribute('style', 'z-index: 20000 !important;');
                },
            });
        }
    };

    const handleClose = () => {
        reset();
        setShowPassword(false);
        setFormErrors({});
        onClose();
    };

    const isLoading = isCreating || isEditing;
    const isDataLoading = mode === 'edit' && isLoadingUser;
    const isDropdownLoading = isLoadingPlants || isLoadingRoles;

    // Determine if submit button should be enabled
    const hasValidationErrors = Object.keys(errors).length > 0 || Object.keys(formErrors).length > 0;
    const isSubmitDisabled = isLoading || hasValidationErrors || (mode === 'edit' && !isDirty);

    // Helper function to safely get error message
    const getErrorMessage = (fieldName: keyof UserFormData): string | undefined => {
        const fieldError = errors[fieldName];
        const formError = formErrors[fieldName as string];

        return fieldError?.message || formError;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                }
            }}
        >
            <BackdropLoader openStates={isLoading} />
            <DialogTitle sx={{
                pb: 2,
                pt: 2.5,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#f8f9fa'
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography component="h2" fontWeight="600" fontSize="16px" sx={{
                        color: '#172B4D',
                        textAlign: 'center',
                        flex: 1,
                    }}>
                        {mode === 'create' ? 'New User Creation' : 'Edit User'}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        aria-label="close"
                        sx={{
                            color: '#666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Display form validation errors summary */}
            {Object.keys(formErrors).length > 0 && (
                <Alert severity="error" sx={{ mx: 3, mt: 2, mb: 1 }}>
                    <Typography variant="body2" fontWeight="500" sx={{ mb: 1 }}>
                        Please fix the following errors:
                    </Typography>
                    {Object.entries(formErrors).map(([field, message]) => (
                        <Typography key={field} variant="caption" sx={{ display: 'block' }}>
                            • {message}
                        </Typography>
                    ))}
                </Alert>
            )}

            {isDataLoading || isDropdownLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            ) : (
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <DialogContent sx={{ pt: 3, pb: 1 }}>
                        {/* User Name & Mobile Number */}
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: '#626F86',
                                        }}
                                    >
                                        User Name <span style={{ color: 'red' }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="userName"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                placeholder="User Name"
                                                fullWidth
                                                size="small"
                                                error={!!errors.userName || !!formErrors.userName}
                                                helperText={getErrorMessage('userName')
                                                    ? (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block', mt: 0,      // remove default top margin
                                                                mb: 0,
                                                                pl: 0,
                                                                fontSize: '0.75rem',
                                                                lineHeight: '0.5rem'
                                                                , color: 'error.main'
                                                            }}
                                                        >
                                                            {getErrorMessage('userName')}
                                                        </Typography>
                                                    ) : null
                                                }
                                                disabled={isLoading}
                                                onBlur={field.onBlur}
                                                sx={{
                                                    display: 'flex',
                                                    height: '40px',
                                                    padding: '0 var(--space_05, 12px)',
                                                    alignItems: 'center',
                                                    gap: 'var(--space_03, 6px)',
                                                    alignSelf: 'stretch',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--Border-color_border_sec, rgba(9, 30, 66, 0.14))',
                                                    background: 'var(--Background-Neutral-color_bg_neutral, rgba(9, 30, 66, 0.06))',

                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            border: 'none',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: '#626F86',
                                        }}
                                    >
                                        Phone Number <span style={{ color: 'red' }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="phone"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                placeholder="Phone Number"
                                                fullWidth
                                                type="number"
                                                size="small"
                                                error={!!errors.phone || !!formErrors.phone}
                                                helperText={getErrorMessage('phone')
                                                    ? (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block', mt: 0,      // remove default top margin
                                                                mb: 0,
                                                                pl: 0,
                                                                fontSize: '0.75rem',
                                                                lineHeight: '0.5rem'
                                                                , color: 'error.main'
                                                            }}
                                                        >
                                                            {getErrorMessage('phone')}
                                                        </Typography>
                                                    ) : null
                                                }
                                                disabled={isLoading}
                                                onBlur={field.onBlur}
                                                inputProps={{
                                                    maxLength: 10,
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*',
                                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                                        if (
                                                            ['e', 'E', '+', '-', '.', ' '].includes(e.key) ||
                                                            (e.key === 'Backspace' && field.value?.length === 0)
                                                        ) {
                                                            e.preventDefault();
                                                        }
                                                    },
                                                }}
                                                sx={{
                                                    display: 'flex',
                                                    height: '40px',
                                                    padding: '0 var(--space_05, 12px)',
                                                    alignItems: 'center',
                                                    gap: 'var(--space_03, 6px)',
                                                    alignSelf: 'stretch',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--Border-color_border_sec, rgba(9, 30, 66, 0.14))',
                                                    background: 'var(--Background-Neutral-color_bg_neutral, rgba(9, 30, 66, 0.06))',

                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            border: 'none',
                                                        },
                                                    },
                                                    // Hide the number input arrows
                                                    '& input[type=number]': {
                                                        MozAppearance: 'textfield',
                                                    },
                                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },

                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Employee ID & Email */}
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: '#626F86',
                                        }}
                                    >
                                        Employee ID <span style={{ color: 'red' }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="employeeId"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                placeholder="Employee ID"
                                                fullWidth
                                                size="small"
                                                error={!!errors.employeeId || !!formErrors.employeeId}
                                                helperText={getErrorMessage('employeeId') ? (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block', mt: 0,      // remove default top margin
                                                            mb: 0,
                                                            pl: 0,
                                                            fontSize: '0.75rem',
                                                            lineHeight: '0.5rem'
                                                            , color: 'error.main'
                                                        }}
                                                    >
                                                        {getErrorMessage('employeeId')}
                                                    </Typography>
                                                ) : null}
                                                disabled={isLoading}
                                                onBlur={field.onBlur}
                                                sx={{
                                                    display: 'flex',
                                                    height: '40px',
                                                    padding: '0 var(--space_05, 12px)',
                                                    alignItems: 'center',
                                                    gap: 'var(--space_03, 6px)',
                                                    alignSelf: 'stretch',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--Border-color_border_sec, rgba(9, 30, 66, 0.14))',
                                                    background: 'var(--Background-Neutral-color_bg_neutral, rgba(9, 30, 66, 0.06))',

                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            border: 'none',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: '#626F86',
                                        }}
                                    >
                                        Email <span style={{ color: 'red' }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type="email"
                                                placeholder="Email"
                                                fullWidth
                                                size="small"
                                                error={!!errors.email || !!formErrors.email}
                                                helperText={getErrorMessage('email') ? (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block', mt: 0,      // remove default top margin
                                                            mb: 0,
                                                            pl: 0,
                                                            fontSize: '0.75rem',
                                                            lineHeight: '0.5rem'
                                                            , color: 'error.main'
                                                        }}
                                                    >
                                                        {getErrorMessage('email')}
                                                    </Typography>
                                                ) : null}
                                                disabled={isLoading || mode === 'edit'}
                                                autoComplete="email"
                                                onBlur={field.onBlur}
                                                sx={{
                                                    display: 'flex',
                                                    height: '40px',
                                                    padding: '0 var(--space_05, 12px)',
                                                    alignItems: 'center',
                                                    gap: 'var(--space_03, 6px)',
                                                    alignSelf: 'stretch',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--Border-color_border_sec, rgba(9, 30, 66, 0.14))',
                                                    background: 'var(--Background-Neutral-color_bg_neutral, rgba(9, 30, 66, 0.06))',

                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            border: 'none',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Plant & Designation */}
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: '#626F86',
                                        }}
                                    >
                                        Plant <span style={{ color: 'red' }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="plant"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                select
                                                placeholder="Select Plant"
                                                fullWidth
                                                size="small"
                                                error={!!errors.plant || !!formErrors.plant}
                                                helperText={getErrorMessage('plant') ? (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block', mt: 0,      // remove default top margin
                                                            mb: 0,
                                                            pl: 0,
                                                            fontSize: '0.75rem',
                                                            lineHeight: '0.5rem'
                                                            , color: 'error.main'
                                                        }}
                                                    >
                                                        {getErrorMessage('plant')}
                                                    </Typography>
                                                ) : null}
                                                disabled={isLoading}
                                                onBlur={field.onBlur}
                                                sx={{
                                                    display: 'flex',
                                                    height: '40px',
                                                    padding: '0 var(--space_05, 12px)',
                                                    alignItems: 'center',
                                                    gap: 'var(--space_03, 6px)',
                                                    alignSelf: 'stretch',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--Border-color_border_sec, rgba(9, 30, 66, 0.14))',
                                                    background: 'var(--Background-Neutral-color_bg_neutral, rgba(9, 30, 66, 0.06))',

                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            border: 'none',
                                                        },
                                                    },
                                                }}
                                            >
                                                {plants.map((option: PlantOption) => (
                                                    <MenuItem
                                                        key={option.id}
                                                        value={String(option.id)}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: '#626F86',
                                        }}
                                    >
                                        Designation <span style={{ color: 'red' }}>*</span>
                                    </Typography>
                                    <Controller
                                        name="designation"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                select
                                                placeholder="Select Designation"
                                                fullWidth
                                                size="small"
                                                error={!!errors.designation || !!formErrors.designation}
                                                helperText={getErrorMessage('designation') ? (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block', mt: 0,      // remove default top margin
                                                            mb: 0,
                                                            pl: 0,
                                                            fontSize: '0.75rem',
                                                            lineHeight: '0.5rem'
                                                            , color: 'error.main'
                                                        }}
                                                    >
                                                        {getErrorMessage('designation')}
                                                    </Typography>
                                                ) : null}
                                                disabled={isLoading}
                                                onBlur={field.onBlur}
                                                sx={{
                                                    display: 'flex',
                                                    height: '40px',
                                                    padding: '0 var(--space_05, 12px)',
                                                    alignItems: 'center',
                                                    gap: 'var(--space_03, 6px)',
                                                    alignSelf: 'stretch',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--Border-color_border_sec, rgba(9, 30, 66, 0.14))',
                                                    background: 'var(--Background-Neutral-color_bg_neutral, rgba(9, 30, 66, 0.06))',

                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            border: 'none',
                                                        },
                                                    },
                                                }}
                                            >
                                                {designations.map((option: RoleOption) => (
                                                    <MenuItem
                                                        key={option.id}
                                                        value={String(option.id)}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Password */}
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            color: '#626F86',
                                        }}
                                    >
                                        Password {mode === 'create' && <span style={{ color: 'red' }}>*</span>}
                                        {mode === 'edit' && <span style={{ color: '#666', fontSize: '12px' }}> (Optional)</span>}
                                    </Typography>
                                    <Controller
                                        name="password"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                type={showPassword ? "text" : "password"}
                                                placeholder={mode === 'create' ? "Password" : "Leave blank or enter new to change"}
                                                fullWidth
                                                size="small"
                                                error={!!errors.password || !!formErrors.password}
                                                helperText={getErrorMessage('password')}
                                                disabled={isLoading}
                                                autoComplete="new-password"
                                                onBlur={field.onBlur}
                                                sx={{
                                                    display: 'flex',
                                                    height: '40px',
                                                    padding: '0 var(--space_05, 12px)',
                                                    alignItems: 'center',
                                                    gap: 'var(--space_03, 6px)',
                                                    alignSelf: 'stretch',
                                                    borderRadius: '8px',
                                                    border: '1px solid var(--Border-color_border_sec, rgba(9, 30, 66, 0.14))',
                                                    background: 'var(--Background-Neutral-color_bg_neutral, rgba(9, 30, 66, 0.06))',

                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                        '& fieldset': {
                                                            border: 'none',
                                                        },
                                                    },
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowPassword((prev) => !prev)}
                                                                edge="end"
                                                                size="small"
                                                            >
                                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    {/* Empty grid for alignment */}
                                </Grid>
                            </Grid>
                        </Box>


                    </DialogContent>

                    <DialogActions sx={{
                        p: 3,
                        gap: 2,
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#f8f9fa'
                    }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            disabled={isLoading}
                            sx={{
                                minWidth: '120px',
                                borderRadius: '4px',
                                textTransform: 'none',
                                fontWeight: '500',
                                color: '#172B4D',
                                border: ' 1px solid var(--Border-color_border, rgba(9, 30, 66, 0.31))',
                                backgroundColor: 'rgba(9, 30, 66, 0.06)',

                                '&:hover': {
                                    borderColor: '#0056b3',
                                    backgroundColor: '#6c757d',
                                },
                                '&:disabled': {
                                    borderColor: '#6c757d',
                                    color: '#6c757d',
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitDisabled}
                            sx={{
                                minWidth: '120px',
                                borderRadius: '4px',
                                textTransform: 'none',
                                fontWeight: '500',
                                backgroundColor: '#003EB0',
                                '&:hover': {
                                    backgroundColor: '#002a7a',
                                },
                                '&:disabled': {
                                    backgroundColor: '#6c757d',
                                }
                            }}
                        >
                            {isLoading ? 'Saving...' : mode === 'create' ? 'Create User' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </form>
            )}
        </Dialog>
    );
};

export default UserFormModal;